import React, { useEffect, useState } from 'react';
import { Price } from '../../services/data/PricingV2Interfaces';
import { FilterOption, PricingCatalogByLeverId } from '../../services/data/PricingCatalogInterfaces';
import PriceTable from './PriceTable';
import { PricingCatalogService } from '../../services/PricingCatalogService';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddNewPriceModal from './AddNewPriceModal';
import { FeeCurrency, InventoryGroup } from '../../CommonInterfaces';

export interface PriceTablesProps {
  prices: Price[];
  pricingCatalog: PricingCatalogByLeverId;
  inventoryGroups: InventoryGroup[];
  disabled: boolean;
  feeCurrency?: FeeCurrency;
  onEditPrice(oldPrice: Price, newPrice: Price): void;
  onDeletePrice(leverId: number, suffix: string, uom: string): void;
  onAddPrice(newPrice: Price): void;
}

interface PricesByCategory {
  [category: string]: Price[];
}

// renders one price table for each category in the given price array
const PriceTables: React.FC<PriceTablesProps> = ({
  prices,
  pricingCatalog,
  disabled,
  feeCurrency,
  onEditPrice,
  onDeletePrice,
  onAddPrice,
  inventoryGroups,
}) => {
  const [pricesByCategory, setPricesByCategory] = useState<PricesByCategory>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [displayNewPriceModal, setDisplayNewPriceModal] = useState<boolean>(false);
  const [priceToUpdate, setPriceToUpdate] = useState<Price | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const pricingCatalogService = new PricingCatalogService();

  useEffect(() => {
    setPricesByCategory(createPricesByCategory(prices));
  }, [prices]);

  useEffect(() => {
    retrieveFilterCatalog();
  }, []);

  function createPricesByCategory(pricesToCategorize: Price[]): PricesByCategory {
    const output: PricesByCategory = {};
    pricesToCategorize.forEach((price) => {
      const category: string = pricingCatalog[price.pricingLeverId]?.product_name;
      if (!output.hasOwnProperty(category)) {
        output[category] = [];
      }
      output[category].push(price);
    });
    return output;
  }

  async function retrieveFilterCatalog() {
    const options = await pricingCatalogService.retrieveFilterCatalog();
    setFilterOptions(options);
    setLoading(false);
  }

  function savePrice(newPrice: Price) {
    if (priceToUpdate) {
      onEditPrice(priceToUpdate, newPrice);
    } else {
      onAddPrice(newPrice);
    }
    setPriceToUpdate(null);
    setDisplayNewPriceModal(false);
  }

  function launchEditModal(leverId: number, suffix: string, uom: string): void {
    const priceToEdit = prices.find(
      (price) =>
        price.pricingLeverId === leverId && price.rateDescriptionSuffix === suffix && price.unitOfMeasure === uom,
    );
    launchNewPriceModal(priceToEdit);
  }

  function launchNewPriceModal(priceToEdit?: Price) {
    setPriceToUpdate(priceToEdit);
    setDisplayNewPriceModal(true);
  }

  function closeNewPriceModal() {
    setDisplayNewPriceModal(false);
  }

  return (
    <div data-testid="price-tables">
      {displayNewPriceModal && (
        <AddNewPriceModal
          priceToUpdate={priceToUpdate}
          pricingCatalog={pricingCatalog}
          filterOptions={filterOptions}
          inventoryGroups={inventoryGroups}
          onSubmitButtonClick={savePrice}
          onCloseClicked={closeNewPriceModal}
        />
      )}
      {!disabled && (
        <div className="add-pricing-lever-div">
          <Button
            className="add-pricing-lever-button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => launchNewPriceModal(null)}
          >
            Add Pricing Lever
          </Button>
        </div>
      )}
      <div>
        {Object.keys(pricesByCategory).map((category) => {
          return (
            <Box key={category} sx={{ mt: 4 }}>
              <PriceTable
                title={category}
                prices={pricesByCategory[category]}
                pricingCatalog={pricingCatalog}
                loading={loading}
                filterOptions={filterOptions}
                disabled={disabled}
                feeCurrency={feeCurrency}
                onEditPrice={launchEditModal}
                onDeletePrice={onDeletePrice}
              />
            </Box>
          );
        })}
      </div>
    </div>
  );
};

export default PriceTables;
